import { useEffect, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MapIcon from '@mui/icons-material/Map';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';

import { useDuplicateMapTemplate } from 'hooks/api/builder/mapTemplates/useDuplicateMapTemplate';
import { useGetTemplateConfig } from 'hooks/api/builder/mapTemplates/useGetTemplateConfig';
import { useGetTemplate } from 'hooks/api/builder/mapTemplates/useGetTemplates';
import { useUpdateTemplate } from 'hooks/api/builder/mapTemplates/useUpdateTemplate';
import { MapTemplateList } from 'types/api/mapTemplateList';

import { MapEditor } from '../MapConfigCreator/mapEditor';
import { MapTemplateCreator } from './MapTemplateCreator';
import { TemplateDeleteModal } from './TemplateDeleteModal';

type MapTemplatesProps = {
    onSave?: (id: number) => void;
};

export const MapTemplates = ({
    onSave = () => undefined,
}: MapTemplatesProps): JSX.Element => {
    const [open, setOpen] = useState(false);

    const [selectedTemplate, setSelectedTemplate] = useState<MapTemplateList>();

    const { templates, loading, getAllTemplates } = useGetTemplate();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const [mapEditorOpen, setMapEditorOpen] = useState(false);

    const {
        loading: configLoading,
        success: configSuccess,
        config,
        getTemplate,
    } = useGetTemplateConfig();

    useEffect(() => {
        if (configSuccess) {
            setMapEditorOpen(true);
        }
    }, [configSuccess]);

    const { success: updateSuccess, updateTemplate } = useUpdateTemplate();

    const {
        duplicateTemplate,
        success: dupeSuccess,
        loading: dupeLoading,
    } = useDuplicateMapTemplate();

    useEffect(() => {
        if (updateSuccess) {
            setMapEditorOpen(false);
            getAllTemplates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateSuccess]);

    useEffect(() => {
        setSelectedTemplate(undefined);
        getAllTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (dupeSuccess) {
            getAllTemplates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dupeSuccess]);

    return (
        <>
            <Button
                variant="outlined"
                onClick={() => setOpen(true)}
                endIcon={<MapIcon />}
            >
                Шаблоны
            </Button>
            <Dialog
                open={open}
                PaperProps={{
                    sx: {
                        maxWidth: 800,
                    },
                }}
            >
                <DialogContent
                    sx={{
                        minWidth: 800,
                        minHeight: 500,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Stack sx={{ flex: 1, overflow: 'auto' }}>
                        {!loading && templates?.length ? (
                            <List>
                                {templates.map((el) => (
                                    <ListItem key={el.id}>
                                        <ListItemButton
                                            selected={
                                                el.id === selectedTemplate?.id
                                            }
                                            onClick={() =>
                                                setSelectedTemplate(el)
                                            }
                                            disabled={
                                                configLoading || dupeLoading
                                            }
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{ width: '100%' }}
                                            >
                                                <ListItemText
                                                    primary={el.name}
                                                />
                                                <IconButton
                                                    onClick={() =>
                                                        duplicateTemplate(el.id)
                                                    }
                                                >
                                                    {dupeLoading &&
                                                    el.id ===
                                                        selectedTemplate?.id ? (
                                                        <CircularProgress
                                                            size={16}
                                                            variant="indeterminate"
                                                        />
                                                    ) : (
                                                        <ContentCopyIcon />
                                                    )}
                                                </IconButton>
                                                <IconButton
                                                    onClick={() =>
                                                        getTemplate(el.id)
                                                    }
                                                >
                                                    {configLoading &&
                                                    el.id ===
                                                        selectedTemplate?.id ? (
                                                        <CircularProgress
                                                            size={16}
                                                            variant="indeterminate"
                                                        />
                                                    ) : (
                                                        <ModeEditIcon />
                                                    )}
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedTemplate(el);
                                                        setDeleteModalOpen(
                                                            true
                                                        );
                                                    }}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Stack>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : undefined}

                        {loading ? (
                            <Stack
                                justifyContent="center"
                                alignItems="center"
                                sx={{ flex: 1 }}
                            >
                                <CircularProgress
                                    size={20}
                                    variant="indeterminate"
                                />
                            </Stack>
                        ) : undefined}
                        {!loading && !templates?.length ? (
                            <Stack
                                justifyContent="center"
                                alignItems="center"
                                sx={{ flex: 1 }}
                            >
                                <Typography>Шаблоны отсутствуют</Typography>
                            </Stack>
                        ) : undefined}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                        <Button variant="text" onClick={() => setOpen(false)}>
                            Отмена
                        </Button>
                        <MapTemplateCreator
                            onSuccess={(temp) => {
                                setSelectedTemplate(temp);
                                getAllTemplates();
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => {
                                setOpen(false);
                                if (selectedTemplate) {
                                    onSave(selectedTemplate?.id);
                                }
                            }}
                            disabled={!selectedTemplate}
                        >
                            Выбрать
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
            {selectedTemplate ? (
                <TemplateDeleteModal
                    id={selectedTemplate.id}
                    onClose={() => setDeleteModalOpen(false)}
                    onSuccess={() => {
                        setSelectedTemplate(undefined);
                        getAllTemplates();
                    }}
                    open={deleteModalOpen}
                    templateTitle={selectedTemplate.name}
                />
            ) : undefined}
            <MapEditor
                defaultConfig={
                    config
                        ? {
                              ...config.config,
                              name: config.name,
                          }
                        : undefined
                }
                onClose={() => setMapEditorOpen(false)}
                open={mapEditorOpen}
                onSave={(template) => {
                    updateTemplate({
                        id: config?.id,
                        uuid: config?.uuid,
                        name: template.name,
                        config: template,
                    });
                }}
            />
        </>
    );
};
